<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="text-h6">
        Referral Promotion Management
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="openPromotionDialog(null)">
          Add Promotion
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="promotions"
          :loading="loading"
          :items-per-page="10"
          class="elevation-1"
        >
          <template v-slot:item.referralValue="{ item }">
            {{ formatCurrency(item.referralValue) }}
          </template>
          <template v-slot:item.startDateTime="{ item }">
            {{ formatDate(item.startDate) }}
          </template>
          <template v-slot:item.endDateTime="{ item }">
            {{ formatDate(item.endDate) }}
          </template>
          <template v-slot:item.active="{ item }">
            <v-chip
              :color="isPromotionActive(item) ? 'success' : 'error'"
              text-color="white"
              small
            >
              {{ isPromotionActive(item) ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small class="mr-2" @click="openPromotionDialog(item)">
              mdi-pencil
            </v-icon>
            <v-icon small @click="deletePromotion(item)">
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Promotion Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editedId ? 'Edit Promotion' : 'New Promotion' }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedPromotion.promotionText"
                  label="Promotion Text"
                  required
                  :error-messages="validationErrors.promotionText"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model.number="editedPromotion.referralValue"
                  label="Referral Value (cents)"
                  hint="4000 is $40, while 40 is $0.40"
                  persistent-hint
                  type="number"
                  min="0"
                  prefix=""
                  :error-messages="validationErrors.referralValue"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedPromotion.match"
                  label="Match"
                  hint="Optional matching value for this promotion.  Referral will only be granted if this text is in the plan id, eg, 'toronto' or 'calgary'"
                  persistent-hint
                  :error-messages="validationErrors.match"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedStartDate"
                      label="Start Date"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="validationErrors.startDate"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="editedPromotion.startDate"
                    @input="startDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="6">
                <v-dialog
                  ref="startTimeDialog"
                  v-model="startTimeMenu"
                  :return-value.sync="editedPromotion.startTime"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="editedPromotion.startTime"
                      label="Start Time"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="validationErrors.startTime"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="startTimeMenu"
                    v-model="editedPromotion.startTime"
                    full-width
                    format="24hr"
                    @click:minute="$refs.startTimeDialog.save(editedPromotion.startTime)"
                  ></v-time-picker>
                </v-dialog>
              </v-col>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedEndDate"
                      label="End Date"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="validationErrors.endDate"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="editedPromotion.endDate"
                    @input="endDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="6">
                <v-dialog
                  ref="endTimeDialog"
                  v-model="endTimeMenu"
                  :return-value.sync="editedPromotion.endTime"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="editedPromotion.endTime"
                      label="End Time"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="validationErrors.endTime"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="endTimeMenu"
                    v-model="editedPromotion.endTime"
                    full-width
                    format="24hr"
                    @click:minute="$refs.endTimeDialog.save(editedPromotion.endTime)"
                  ></v-time-picker>
                </v-dialog>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="dialog = false">
            Cancel
          </v-btn>
          <v-btn color="blue darken-1" text @click="savePromotion" :loading="saving">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Dialog -->
    <v-dialog v-model="confirmDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5">Confirm Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to delete this promotion? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="red darken-1" text @click="confirmDeletePromotion" :loading="deleting">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from '../api';
import moment from 'moment';
import {formatCurrency} from "../store/utils";

export default {
  name: 'Promotions',
  data() {
    return {
      promotions: [],
      loading: false,
      saving: false,
      deleting: false,
      dialog: false,
      confirmDialog: false,
      startDateMenu: false,
      endDateMenu: false,
      startTimeMenu: false,
      endTimeMenu: false,
      editedId: null,
      deletingId: null,
      editedPromotion: this.getDefaultPromotion(),
      headers: [
        { text: 'Promotion Text', value: 'promotionText' },
        { text: 'Referral Value', value: 'referralValue' },
        { text: 'Match', value: 'match' },
        { text: 'Start Date/Time', value: 'startDateTime' },
        { text: 'End Date/Time', value: 'endDateTime' },
        { text: 'Status', value: 'active' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      validationErrors: {
        promotionText: '',
        referralValue: '',
        match: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: ''
      }
    };
  },
  computed: {
    formattedStartDate() {
      return this.editedPromotion.startDate ? moment(this.editedPromotion.startDate).format('YYYY-MM-DD') : '';
    },
    formattedEndDate() {
      return this.editedPromotion.endDate ? moment(this.editedPromotion.endDate).format('YYYY-MM-DD') : '';
    }
  },
  mounted() {
    this.fetchPromotions();
  },
  methods: {
    formatCurrency,
    getDefaultPromotion() {
      return {
        promotionText: '',
        referralValue: 0,
        match: '',
        startDate: moment().format('YYYY-MM-DD'),
        startTime: '00:00',
        endDate: moment().add(30, 'days').format('YYYY-MM-DD'),
        endTime: '23:59'
      };
    },
    async fetchPromotions() {
      this.loading = true;
      try {
        const response = await api.get('/v2/promotions');
        console.log('Received promotions from API:', response.data);
        
        // Ensure all dates are properly converted to ISO strings if they aren't already
        this.promotions = response.data.map(promotion => {
          if (promotion.startDate && !moment(promotion.startDate).isValid()) {
            console.warn('Invalid startDate format received:', promotion.startDate);
          }
          if (promotion.endDate && !moment(promotion.endDate).isValid()) {
            console.warn('Invalid endDate format received:', promotion.endDate);
          }
          return promotion;
        });
      } catch (error) {
        console.error('Error fetching promotions:', error);
        this.$store.dispatch('setSnackbar', {
          text: `Error loading promotions: ${error.response?.data?.error || error.message}`,
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
    openPromotionDialog(promotion) {
      this.clearValidationErrors();
      this.editedId = promotion ? promotion.id : null;
      this.editedPromotion = promotion 
        ? this.parseDateTime(promotion)
        : this.getDefaultPromotion();
      this.dialog = true;
    },
    async savePromotion() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        // Combine date and time into ISO strings
        const startDateTime = this.combineDateTime(
          this.editedPromotion.startDate, 
          this.editedPromotion.startTime
        );
        const endDateTime = this.combineDateTime(
          this.editedPromotion.endDate, 
          this.editedPromotion.endTime
        );

        console.log('Start date-time:', startDateTime);
        console.log('End date-time:', endDateTime);

        const promotionData = {
          promotionText: this.editedPromotion.promotionText,
          referralValue: this.editedPromotion.referralValue,
          match: this.editedPromotion.match || '',
          startDate: startDateTime,
          endDate: endDateTime
        };
        
        console.log('Sending promotion data:', promotionData);

        if (this.editedId) {
          // Update existing promotion
          await api.put(`/v2/promotions/${this.editedId}`, promotionData);
          this.$store.dispatch('setSnackbar', {
            text: 'Promotion updated successfully',
            color: 'success'
          });
        } else {
          // Create new promotion
          await api.post('/v2/promotions', promotionData);
          this.$store.dispatch('setSnackbar', {
            text: 'Promotion created successfully',
            color: 'success'
          });
        }
        this.dialog = false;
        this.fetchPromotions();
      } catch (error) {
        console.error('Error saving promotion:', error);
        this.$store.dispatch('setSnackbar', {
          text: `Error saving promotion: ${error.response?.data?.error || error.message}`,
          color: 'error'
        });
      } finally {
        this.saving = false;
      }
    },
    
    combineDateTime(date, time) {
      // Ensure both date and time exist
      if (!date || !time) {
        console.error('Missing date or time in combineDateTime:', { date, time });
        return date; // Return just the date as fallback
      }
      
      // Format to ensure it's a valid ISO string
      try {
        const combinedDateTime = `${date}T${time}:00`;
        // Validate by creating a moment object
        const momentDateTime = moment(combinedDateTime);
        if (!momentDateTime.isValid()) {
          console.error('Invalid date-time combination:', combinedDateTime);
          return date;
        }
        return momentDateTime.toISOString(); // Return full ISO string
      } catch (error) {
        console.error('Error combining date and time:', error);
        return date;
      }
    },
    deletePromotion(promotion) {
      this.deletingId = promotion.id;
      this.confirmDialog = true;
    },
    async confirmDeletePromotion() {
      this.deleting = true;
      try {
        await api.delete(`/v2/promotions/${this.deletingId}`);
        this.$store.dispatch('setSnackbar', {
          text: 'Promotion deleted successfully',
          color: 'success'
        });
        this.confirmDialog = false;
        this.fetchPromotions();
      } catch (error) {
        console.error('Error deleting promotion:', error);
        this.$store.dispatch('setSnackbar', {
          text: `Error deleting promotion: ${error.response?.data?.error || error.message}`,
          color: 'error'
        });
      } finally {
        this.deleting = false;
      }
    },
    formatDate(dateString) {
      return moment(dateString).format('YYYY-MM-DD HH:mm');
    },
    isPromotionActive(promotion) {
      const now = moment();
      const startDate = moment(promotion.startDate);
      const endDate = moment(promotion.endDate);
      
      return now.isSameOrAfter(startDate) && now.isSameOrBefore(endDate);
    },
    parseDateTime(promotion) {
      if (promotion && promotion.startDate) {
        // If receiving from the API
        try {
          console.log('Parsing promotion from API:', promotion);
          
          const start = moment(promotion.startDate);
          const end = moment(promotion.endDate);
          
          console.log('Start moment object valid:', start.isValid());
          console.log('End moment object valid:', end.isValid());
          
          const parsedPromotion = {
            ...promotion,
            startDate: start.format('YYYY-MM-DD'),
            startTime: start.format('HH:mm'),
            endDate: end.format('YYYY-MM-DD'),
            endTime: end.format('HH:mm')
          };
          
          console.log('Parsed promotion for editing:', parsedPromotion);
          return parsedPromotion;
        } catch (error) {
          console.error('Error parsing date-time:', error);
          return promotion;
        }
      }
      return promotion;
    },
    validateForm() {
      this.clearValidationErrors();
      let isValid = true;

      if (!this.editedPromotion.promotionText) {
        this.validationErrors.promotionText = 'Promotion text is required';
        isValid = false;
      }

      if (this.editedPromotion.referralValue === null || 
          this.editedPromotion.referralValue === undefined || 
          this.editedPromotion.referralValue < 0) {
        this.validationErrors.referralValue = 'Referral value must be a positive number';
        isValid = false;
      }

      if (!this.editedPromotion.startDate) {
        this.validationErrors.startDate = 'Start date is required';
        isValid = false;
      }

      if (!this.editedPromotion.startTime) {
        this.validationErrors.startTime = 'Start time is required';
        isValid = false;
      }

      if (!this.editedPromotion.endDate) {
        this.validationErrors.endDate = 'End date is required';
        isValid = false;
      }

      if (!this.editedPromotion.endTime) {
        this.validationErrors.endTime = 'End time is required';
        isValid = false;
      }

      if (this.editedPromotion.startDate && this.editedPromotion.endDate) {
        // Combine date and time for comparison
        const startDateTime = moment(this.combineDateTime(
          this.editedPromotion.startDate, 
          this.editedPromotion.startTime
        ));
        
        const endDateTime = moment(this.combineDateTime(
          this.editedPromotion.endDate, 
          this.editedPromotion.endTime
        ));
        
        if (endDateTime.isSameOrBefore(startDateTime)) {
          this.validationErrors.endDate = 'End date/time must be after the start date/time';
          isValid = false;
        }
      }

      return isValid;
    },
    clearValidationErrors() {
      this.validationErrors = {
        promotionText: '',
        referralValue: '',
        match: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: ''
      };
    }
  }
};
</script>

<style scoped>
.v-data-table ::v-deep th {
  font-weight: bold;
}
</style>